import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  Plus, 
  Search, 
  RefreshCw, 
  Trash2, 
  Settings, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader2,
  ExternalLink,
  Filter
} from 'lucide-react';
import { 
  getUserIntegrations, 
  INTEGRATION_PROVIDERS, 
  Integration, 
  IntegrationProvider,
  deleteIntegration,
  syncIntegrationData
} from '../lib/integrations';
import { useNavigate } from 'react-router-dom';

const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserIntegrations();
      setIntegrations(data);
    } catch (err) {
      console.error('Error fetching integrations:', err);
      setError('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIntegration = () => {
    navigate('/integrations/new');
  };

  const handleEditIntegration = (id: string) => {
    navigate(`/integrations/${id}`);
  };

  const handleSyncIntegration = async (id: string) => {
    try {
      setSyncingId(id);
      const result = await syncIntegrationData(id);
      
      if (result.success) {
        // Show success message
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        notification.innerHTML = `
          <div class="flex items-center space-x-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>${result.message}</span>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 5000);
        
        // Refresh the integrations list
        fetchIntegrations();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error syncing integration:', err);
      setError('Failed to sync integration data');
    } finally {
      setSyncingId(null);
    }
  };

  const handleDeleteIntegration = async (id: string) => {
    if (!confirm('Are you sure you want to delete this integration? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeletingId(id);
      const success = await deleteIntegration(id);
      
      if (success) {
        // Remove the integration from the list
        setIntegrations(integrations.filter(integration => integration.id !== id));
        
        // Show success message
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        notification.innerHTML = `
          <div class="flex items-center space-x-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Integration deleted successfully</span>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 5000);
      } else {
        setError('Failed to delete integration');
      }
    } catch (err) {
      console.error('Error deleting integration:', err);
      setError('Failed to delete integration');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-green-900 text-green-300 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            <span>Active</span>
          </div>
        );
      case 'inactive':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
            <XCircle className="w-3 h-3" />
            <span>Inactive</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-red-900 text-red-300 rounded-full text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>Error</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
            <span>Unknown</span>
          </div>
        );
    }
  };

  const getProviderDetails = (providerId: string): IntegrationProvider | undefined => {
    return INTEGRATION_PROVIDERS.find(provider => provider.id === providerId);
  };

  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         integration.provider.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || 
                           getProviderDetails(integration.provider)?.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Zap className="w-8 h-8 text-indigo-400" />
            <span>Integrations</span>
          </h1>
          <p className="text-gray-400">Connect ADAPT with your existing tools and services</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button
            onClick={handleAddIntegration}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Integration</span>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-900 border border-red-700 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none text-sm lg:text-base"
          />
        </div>
        <div className="flex items-center space-x-2 bg-gray-800 px-3 py-2 rounded-lg border border-gray-700">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="bg-transparent text-white border-none focus:outline-none text-sm"
          >
            <option value="all">All Categories</option>
            <option value="project-management">Project Management</option>
            <option value="analytics">Analytics</option>
            <option value="communication">Communication</option>
            <option value="development">Development</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Integrations list */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-12 h-12 text-indigo-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">Loading integrations</h3>
          <p className="text-gray-500">Please wait while we fetch your integrations</p>
        </div>
      ) : filteredIntegrations.length > 0 ? (
        <div className="space-y-4">
          {filteredIntegrations.map((integration) => {
            const provider = getProviderDetails(integration.provider);
            
            return (
              <div key={integration.id} className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700 hover:border-gray-600 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Integration info */}
                  <div className="flex items-center space-x-4">
                    {provider?.logo ? (
                      <img 
                        src={provider.logo} 
                        alt={provider.name} 
                        className="w-10 h-10 object-contain bg-white p-1 rounded-lg"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                        <Zap className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-white">{integration.name}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        {getStatusBadge(integration.status)}
                        <span className="text-xs text-gray-400">
                          {integration.last_sync 
                            ? `Last synced: ${new Date(integration.last_sync).toLocaleString()}` 
                            : 'Never synced'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSyncIntegration(integration.id)}
                      disabled={syncingId === integration.id}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {syncingId === integration.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEditIntegration(integration.id)}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteIntegration(integration.id)}
                      disabled={deletingId === integration.id}
                      className="p-2 bg-red-900 hover:bg-red-800 text-red-300 hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingId === integration.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <Zap className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No integrations found</h3>
          <p className="text-gray-500 mb-6">Get started by connecting ADAPT with your favorite tools</p>
          <button
            onClick={handleAddIntegration}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 mx-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Add Your First Integration</span>
          </button>
        </div>
      )}

      {/* Available Integrations */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-white mb-4">Available Integrations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {INTEGRATION_PROVIDERS.map((provider) => (
            <div key={provider.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-indigo-500 transition-colors">
              <div className="flex items-center space-x-3 mb-3">
                {provider.logo ? (
                  <img 
                    src={provider.logo} 
                    alt={provider.name} 
                    className="w-8 h-8 object-contain bg-white p-1 rounded-lg"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-white">{provider.name}</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">{provider.description}</p>
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-2">Capabilities:</div>
                <ul className="space-y-1">
                  {provider.capabilities.slice(0, 2).map((capability, index) => (
                    <li key={index} className="text-xs text-gray-300 flex items-start">
                      <CheckCircle className="w-3 h-3 text-green-400 mr-1 flex-shrink-0 mt-0.5" />
                      <span>{capability}</span>
                    </li>
                  ))}
                  {provider.capabilities.length > 2 && (
                    <li className="text-xs text-indigo-400">
                      +{provider.capabilities.length - 2} more capabilities
                    </li>
                  )}
                </ul>
              </div>
              <button
                onClick={() => navigate(`/integrations/new?provider=${provider.id}`)}
                className="w-full py-2 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Connect</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Documentation */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mt-8">
        <h2 className="text-xl font-semibold text-white mb-4">Integration Documentation</h2>
        <p className="text-gray-400 mb-4">
          Learn how to get the most out of ADAPT integrations with our comprehensive documentation.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <a 
            href="#" 
            className="bg-gray-700 hover:bg-gray-600 p-4 rounded-lg transition-colors flex items-center space-x-3"
          >
            <div className="w-8 h-8 bg-indigo-900 rounded-lg flex items-center justify-center">
              <ExternalLink className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">Getting Started</h3>
              <p className="text-xs text-gray-400">Learn the basics of integrations</p>
            </div>
          </a>
          <a 
            href="#" 
            className="bg-gray-700 hover:bg-gray-600 p-4 rounded-lg transition-colors flex items-center space-x-3"
          >
            <div className="w-8 h-8 bg-green-900 rounded-lg flex items-center justify-center">
              <ExternalLink className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">API Documentation</h3>
              <p className="text-xs text-gray-400">Explore our API endpoints</p>
            </div>
          </a>
          <a 
            href="#" 
            className="bg-gray-700 hover:bg-gray-600 p-4 rounded-lg transition-colors flex items-center space-x-3"
          >
            <div className="w-8 h-8 bg-purple-900 rounded-lg flex items-center justify-center">
              <ExternalLink className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">Webhooks Guide</h3>
              <p className="text-xs text-gray-400">Set up and manage webhooks</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;